import { useReactToPrint } from "react-to-print";
import { useRef } from "react";
import { Layout } from "@/components/layout";
import { ProtectedRoute } from "@/components/auth";
import { PrintableConsultation } from "@/components/travelers/print/PrintableConsultation";
import { PrintableMedicationPrescription } from "@/components/travelers/print/PrintableMedicationPrescription";
import { useTravelerReview } from "@/hooks/useTravelerReview";
import { ReviewHeader } from "@/components/travelers/ReviewHeader";
import { ActionButtons } from "@/components/travelers/ActionButtons";
import { QuickNoteSection } from "@/components/travelers/QuickNoteSection";
import { AlertNotification } from "@/components/travelers/AlertNotification";
import { NotesSection } from "@/components/travelers/NotesSection";
import { PersonalInfoSection } from "@/components/travelers/PersonalInfoSection";
import { TripDetailsSection } from "@/components/travelers/TripDetailsSection";
import { MedicalSummarySection } from "@/components/travelers/MedicalSummarySection";
import { MedicationsSection } from "@/components/travelers/MedicationsSection";
import { MedicationChecklistSection } from "@/components/travelers/MedicationChecklistSection";
import { VaccineStatusSection } from "@/components/travelers/VaccineStatusSection";
import { AdministrationsSection } from "@/components/travelers/AdministrationsSection";
import { ConsultationRequirementsSection } from "@/components/travelers/ConsultationRequirementsSection";
import { TravelPlansSection } from "@/components/travelers/TravelPlansSection";
import { DestinationHealthInfoSection } from "@/components/travelers/DestinationHealthInfoSection";
import { FloatingHealthRecommendations } from "@/components/travelers/FloatingHealthRecommendations";
import { ReviewModals } from "@/components/travelers/ReviewModals";

export default function TravelerFormReview() {
  const {
    formData,
    loading,
    error,
    flagReason,
    setFlagReason,
    noteContent,
    setNoteContent,
    showFlagModal,
    setShowFlagModal,
    showNoteModal,
    setShowNoteModal,
    showConfirmModal,
    setShowConfirmModal,
    processing,
    showQuickNote,
    setShowQuickNote,
    quickNoteContent,
    setQuickNoteContent,
    destinationInfoMap,
    loadingDestinations,
    expandedDestinations,
    medicationSelections,
    alertState,
    setAlertState,
    printRef,
    isInIframe,
    isViewMode,
    handleApprove,
    confirmApprove,
    handleFlag,
    handleAddNote,
    handleQuickNote,
    handleBackNavigation,
    toggleDestination,
    handleMedicationSelectionChange,
    medicationNotes,
    handleMedicationNoteChange,
    saveItemNote,
    saveSelections,
    refillInfos,
    administrationSites,
    quantities,
    handleRefillInfoChange,
    handleAdministrationSiteChange,
    handleQuantityChange,
    saveItemDetails,
    aiRecommendations,
    savingSelections,
    selectionVersion,
    setAiRecommendations,
    fetchTravelerForm,
    pharmacistRecommendationNote,
    handleRecommendationNoteChange,
  } = useTravelerReview();

  const medicationPrintRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${formData?.consultationNumber || "Consultation"}_${
      formData?.travelerName || "Traveler"
    }_Form`,
    pageStyle: `
      @page {
        size: auto;
        margin: 20mm;
      }
      html, body {
        width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  const handlePrintMedications = useReactToPrint({
    contentRef: medicationPrintRef,
    documentTitle: `${formData?.consultationNumber || "Consultation"}_${
      formData?.travelerName || "Traveler"
    }_Prescription`,
    pageStyle: `
      @page {
        size: auto;
        margin: 10mm;
      }
      html, body {
        width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        overflow: visible !important;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-4"></div>
              <p className="text-lg text-gray-700 font-medium">
                Loading consultation details...
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Please wait while we fetch the data
              </p>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  if (error) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Oops! Something went wrong
              </h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  if (!formData) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md text-center">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Form Not Found
              </h3>
              <p className="text-gray-600 mb-6">
                The consultation form you're looking for doesn't exist or has
                been removed.
              </p>
              <button
                onClick={() => window.history.back()}
                className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Go Back
              </button>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="traveler-form-review">
          <AlertNotification
            show={alertState.show}
            variant={alertState.variant}
            title={alertState.title}
            message={alertState.message}
            onClose={() => setAlertState((prev) => ({ ...prev, show: false }))}
          />

          <ReviewHeader formData={formData} isInIframe={isInIframe} />

          <ActionButtons
            formData={formData}
            isViewMode={isViewMode}
            isInIframe={isInIframe}
            processing={processing}
            showQuickNote={showQuickNote}
            onApprove={handleApprove}
            onFlag={() => setShowFlagModal(true)}
            onQuickNote={() => setShowQuickNote(!showQuickNote)}
            onDetailedNote={() => setShowNoteModal(true)}
            onPrint={handlePrint}
            onPrintMedications={handlePrintMedications}
            onBack={handleBackNavigation}
          />

          <QuickNoteSection
            show={!isViewMode && showQuickNote}
            content={quickNoteContent}
            processing={processing}
            onContentChange={setQuickNoteContent}
            onSave={handleQuickNote}
            onCancel={() => {
              setShowQuickNote(false);
              setQuickNoteContent("");
            }}
          />

          {/* Pharmacy Notes - Prominently displayed at the top */}
          <NotesSection formData={formData} />

          {/* AI Medication Recommendations - Top Section */}
          <MedicationChecklistSection
            destinationInfoMap={destinationInfoMap}
            medicationSelections={medicationSelections}
            onSelectionChange={handleMedicationSelectionChange}
            medicationNotes={medicationNotes}
            onNoteChange={handleMedicationNoteChange}
            onSaveNote={saveItemNote}
            isLoading={loadingDestinations}
            travelerId={formData.travelerId}
            aiRecommendations={aiRecommendations}
            onSaveSelections={saveSelections}
            savingSelections={savingSelections}
            onRefresh={fetchTravelerForm}
            selections={formData.selections}
            pharmacistRecommendationNote={pharmacistRecommendationNote}
            onRecommendationNoteChange={handleRecommendationNoteChange}
            refillInfos={refillInfos}
            administrationSites={administrationSites}
            quantities={quantities}
            onRefillInfoChange={handleRefillInfoChange}
            onAdministrationSiteChange={handleAdministrationSiteChange}
            onQuantityChange={handleQuantityChange}
            onSaveDetails={saveItemDetails}
          />

          <div ref={printRef} className="print-only-container">
            <PrintableConsultation formData={formData} selectionVersion={selectionVersion} aiRecommendations={aiRecommendations} />
          </div>

          <div>
            <div className="overview-cards">
              <PersonalInfoSection formData={formData} />
              <TripDetailsSection formData={formData} />
              <MedicalSummarySection formData={formData} />
              <MedicationsSection formData={formData} />
              <VaccineStatusSection formData={formData} />
              <AdministrationsSection travelerId={formData.travelerId} />
              <ConsultationRequirementsSection formData={formData} />
              <TravelPlansSection formData={formData} />
            </div>

            <DestinationHealthInfoSection
              formData={formData}
              destinationInfoMap={destinationInfoMap}
              loadingDestinations={loadingDestinations}
              expandedDestinations={expandedDestinations}
              toggleDestination={toggleDestination}
            />

            {formData.formData?.pharmacy_info && (
              <div className="info-card pharmacy-info">
                <h3>Insurance & Pharmacy Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Insurance Provider:</label>
                    <span>
                      {formData.formData.pharmacy_info.insurance_provider}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Member ID:</label>
                    <span>
                      {formData.formData.pharmacy_info.insurance_member_id}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Preferred Pharmacy:</label>
                    <span>
                      {formData.formData.pharmacy_info.preferred_pharmacy}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Pharmacy Address:</label>
                    <span>
                      {formData.formData.pharmacy_info.pharmacy_address}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <ReviewModals
            showFlagModal={showFlagModal}
            showNoteModal={showNoteModal}
            showConfirmModal={showConfirmModal}
            flagReason={flagReason}
            noteContent={noteContent}
            processing={processing}
            onFlagReasonChange={setFlagReason}
            onNoteContentChange={setNoteContent}
            onFlagSubmit={handleFlag}
            onNoteSubmit={handleAddNote}
            onConfirmApprove={confirmApprove}
            onCloseFlagModal={() => setShowFlagModal(false)}
            onCloseNoteModal={() => setShowNoteModal(false)}
            onCloseConfirmModal={() => setShowConfirmModal(false)}
          />

          {/* Floating Health Recommendations Button */}
          <FloatingHealthRecommendations
            formData={formData}
            destinationInfoMap={destinationInfoMap}
            loadingDestinations={loadingDestinations}
          />

          {/* Hidden Print Components */}
          <div ref={medicationPrintRef} className="print-only-container">
            <PrintableMedicationPrescription
              formData={formData}
              destinationInfoMap={destinationInfoMap}
              selectionVersion={selectionVersion}
              aiRecommendations={aiRecommendations}
            />
          </div>
        </div>
      </Layout>
    </ProtectedRoute>
  );
}
